import {
  CHECKLIST_IMPORT_RESULT_LIMITS as limits,
  CHECKLIST_IMPORT_RESULT_POLICY,
  checklistCatalogDeliveryCanonicalJson as canonical,
  type ChecklistImportResultManifest,
  type ChecklistImportVerificationResponse,
  type ChecklistImportVerificationSummary,
  type ChecklistImportResultEvidence,
  type ChecklistImportProductResult,
  type ChecklistImportVerificationReportResponse,
} from '@tcvs/shared';

type Guard = (value: unknown) => boolean;
const text =
  (pattern: RegExp, max: number): Guard =>
  (v) =>
    typeof v === 'string' && v.length <= max && pattern.test(v);
const id = text(/^[A-Za-z0-9][A-Za-z0-9_.:#-]*$/, 256);
const target = text(/^[0-9]+$/, 64);
const hash = text(/^[a-f0-9]{64}$/, 64);
const date: Guard = (v) =>
  typeof v === 'string' &&
  v.length <= 40 &&
  /^\d{4}-\d\d-\d\dT/.test(v) &&
  Number.isFinite(Date.parse(v));
const count: Guard = (v) => Number.isSafeInteger(v) && (v as number) >= 0;
const bool: Guard = (v) => typeof v === 'boolean';
const one =
  (...values: unknown[]): Guard =>
  (v) =>
    values.includes(v);
const nullable =
  (guard: Guard): Guard =>
  (v) =>
    v === null || guard(v);
const array =
  (guard: Guard, max: number): Guard =>
  (v) =>
    Array.isArray(v) && v.length <= max && v.every(guard);
function shape(required: Record<string, Guard>, optional: Record<string, Guard> = {}): Guard {
  return (v) => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
    const record = v as Record<string, unknown>;
    return (
      Object.keys(record).every(
        (key) => Object.hasOwn(required, key) || Object.hasOwn(optional, key),
      ) &&
      Object.entries(required).every(
        ([key, guard]) => Object.hasOwn(record, key) && guard(record[key]),
      ) &&
      Object.entries(optional).every(
        ([key, guard]) => !Object.hasOwn(record, key) || guard(record[key]),
      )
    );
  };
}
const reason = text(/^[A-Za-z][A-Za-z0-9_-]*$/, 100);
const counts = shape({
  roster: nullable(count),
  parallelDefinitions: nullable(count),
  subsetDefinitions: nullable(count),
});
const diffCount = shape({
  adds: nullable(count),
  updates: nullable(count),
  unchanged: nullable(count),
  sourceMissing: nullable(count),
  sourceMissingDisposition: one('RETAINED', 'REMOVED', 'UNKNOWN'),
});
const evidence = shape({
  receiptId: nullable(id),
  inputSha256: nullable(hash),
  claimedInputSha256: nullable(hash),
  claimedInputMatch: one('MATCH', 'MISMATCH', 'UNKNOWN'),
  expectedStateSha256: nullable(hash),
  baselineRevision: nullable(id),
  completedRevision: nullable(id),
  coverage: one('COMPLETE', 'PARTIAL', 'UNAVAILABLE'),
  reasonCodes: array(reason, 100),
});
const checks = shape(
  Object.fromEntries(
    [
      'input',
      'providerValues',
      'effectiveValues',
      'reciprocalMappings',
      'definitions',
      'retainedState',
    ].map((key) => [key, one('PASS', 'FAIL', 'UNKNOWN')]),
  ),
);
const sample = shape(
  { code: reason, detail: text(/^[A-Za-z0-9 ,.;:()'/-]+$/, 500) },
  { sourceItemId: target, definitionId: id, fieldPath: text(/^\/[A-Za-z0-9_/-]+$/, 256) },
);
const verificationFields = {
  sessionId: nullable(id),
  status: one('NOT_RUN', 'RUNNING', 'VERIFIED_COMPLETE', 'MISMATCH', 'INCOMPLETE', 'UNKNOWN'),
  complete: bool,
  phase: one(
    'NOT_RUN',
    'INPUT',
    'SOURCE_MAPPINGS',
    'RECIPROCAL_REFERENCES',
    'PARALLEL_DEFINITIONS',
    'SUBSET_DEFINITIONS',
    'CORRECTIONS',
    'EXPECTED_VALUES',
    'FINALIZING',
    'COMPLETE',
  ),
  version: count,
  checks,
  observedCounts: counts,
  mismatchTotal: count,
  samples: array(sample, limits.maxSamples),
  samplesTruncated: bool,
  revision: nullable(id),
  verifiedAt: nullable(date),
};
const verification = shape({
  ...verificationFields,
  reportId: nullable(id),
  digest: nullable(hash),
});
const freshness = shape({ status: one('CURRENT', 'STALE', 'UNKNOWN'), checkedAt: date });
const product = shape({
  sourceSystem: one('azure-sql-ebay'),
  sourceTargetId: target,
  jobId: id,
  fingerprint: id,
  mode: one('ordinary', 'replace', 'unknown'),
  importStatus: one('QUEUED', 'PREPARING', 'RUNNING', 'RETRYING', 'COMPLETED', 'FAILED', 'UNKNOWN'),
  inputCounts: counts,
  diff: shape({ roster: diffCount, parallelDefinitions: diffCount, subsetDefinitions: diffCount }),
  evidence,
  verification,
  freshness,
});
const manifestFields = {
  schemaVersion: one(1),
  reportType: one('CHECKLIST_IMPORT_RESULTS'),
  policyVersion: one(CHECKLIST_IMPORT_RESULT_POLICY),
  batchId: id,
  batchFingerprint: id,
  membership: shape(
    {
      coverage: one('COMPLETE', 'PARTIAL', 'UNKNOWN'),
      total: nullable((v) => count(v) && (v as number) <= limits.maxBatchProducts),
      listed: count,
    },
    { nextCursor: text(/^[A-Za-z0-9_=.+/-]+$/, 8192) },
  ),
  complete: bool,
  observedAt: date,
  importCounters: shape(
    Object.fromEntries(
      ['total', 'queued', 'running', 'retrying', 'completed', 'failed'].map((key) => [
        key,
        nullable(count),
      ]),
    ),
  ),
  products: array(product, limits.maxPageSize),
};
const manifest = shape(manifestFields);
function bounded(value: unknown, max: number): boolean {
  try {
    return new TextEncoder().encode(JSON.stringify(value)).length <= max;
  } catch {
    return false;
  }
}
function summaryIsConsistent(
  v: Omit<ChecklistImportVerificationSummary, 'reportId' | 'digest'>,
): boolean {
  return (
    v.mismatchTotal >= v.samples.length &&
    v.samplesTruncated === v.mismatchTotal > v.samples.length &&
    (v.status !== 'VERIFIED_COMPLETE' ||
      (v.complete &&
        v.phase === 'COMPLETE' &&
        v.mismatchTotal === 0 &&
        v.revision !== null &&
        v.verifiedAt !== null &&
        Object.values(v.checks).every((check) => check === 'PASS'))) &&
    (!v.complete || (v.phase === 'COMPLETE' && v.status !== 'RUNNING' && v.status !== 'NOT_RUN'))
  );
}
function evidenceIsConsistent(e: ChecklistImportResultEvidence): boolean {
  return (
    e.claimedInputMatch === 'UNKNOWN' ||
    (e.inputSha256 !== null &&
      e.claimedInputSha256 !== null &&
      (e.claimedInputMatch === 'MATCH'
        ? e.inputSha256 === e.claimedInputSha256
        : e.inputSha256 !== e.claimedInputSha256))
  );
}
function completeEvidence(e: ChecklistImportResultEvidence, revision: string | null): boolean {
  return (
    e.coverage === 'COMPLETE' &&
    e.receiptId !== null &&
    e.inputSha256 !== null &&
    e.expectedStateSha256 !== null &&
    e.baselineRevision !== null &&
    e.completedRevision !== null &&
    e.completedRevision === revision
  );
}
function summariesValid(products: ChecklistImportResultManifest['products']): boolean {
  return products.every(
    ({ verification: v, evidence: e, importStatus }) =>
      summaryIsConsistent(v) &&
      evidenceIsConsistent(e) &&
      (v.status !== 'VERIFIED_COMPLETE' ||
        (importStatus === 'COMPLETED' && completeEvidence(e, v.revision))),
  );
}
export function isImportResultPage(value: unknown): value is ChecklistImportResultManifest {
  if (!manifest(value) || !bounded(value, limits.maxPageBytes)) return false;
  const page = value as ChecklistImportResultManifest;
  return (
    page.membership.listed === page.products.length &&
    (!page.membership.nextCursor || page.products.length > 0) &&
    summariesValid(page.products) &&
    new Set(page.products.map((p) => p.sourceTargetId)).size === page.products.length
  );
}
export function isImportVerificationResponse(
  value: unknown,
  product?: ChecklistImportProductResult,
): value is ChecklistImportVerificationResponse {
  return (
    shape({
      schemaVersion: one(1),
      batchId: id,
      sourceTargetId: target,
      verification,
      freshness,
      expiresAt: date,
    })(value) &&
    bounded(value, limits.maxReportBytes) &&
    summaryIsConsistent((value as ChecklistImportVerificationResponse).verification) &&
    ((value as ChecklistImportVerificationResponse).verification.status !== 'VERIFIED_COMPLETE' ||
      Boolean(
        product &&
        product.importStatus === 'COMPLETED' &&
        completeEvidence(
          product.evidence,
          (value as ChecklistImportVerificationResponse).verification.revision,
        ),
      ))
  );
}
const reportResponse = shape({
  report: shape({
    schemaVersion: one(1),
    reportType: one('CHECKLIST_IMPORT_VERIFICATION'),
    policyVersion: one(CHECKLIST_IMPORT_RESULT_POLICY),
    reportId: id,
    digest: hash,
    batchId: id,
    batchFingerprint: id,
    sourceTargetId: target,
    jobId: id,
    fingerprint: id,
    evidence,
    verification: shape(verificationFields),
    savedReferences: shape({ coverage: one('UNKNOWN'), count: one(null) }),
    applyAllowed: one(false),
    createdAt: date,
    expiresAt: date,
  }),
  freshness,
});
export async function verifyImportResultReport(value: unknown): Promise<boolean> {
  try {
    if (!reportResponse(value) || !bounded(value, limits.maxReportBytes + 1024)) return false;
    const dto = value as ChecklistImportVerificationReportResponse;
    if (
      !summaryIsConsistent(dto.report.verification) ||
      !evidenceIsConsistent(dto.report.evidence) ||
      (dto.report.verification.status === 'VERIFIED_COMPLETE' &&
        !completeEvidence(dto.report.evidence, dto.report.verification.revision))
    )
      return false;
    const { digest, ...unsigned } = dto.report;
    const actual = Array.from(
      new Uint8Array(
        await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical(unsigned))),
      ),
      (byte) => byte.toString(16).padStart(2, '0'),
    ).join('');
    return actual === digest && bounded(dto.report, limits.maxReportBytes);
  } catch {
    return false;
  }
}
/** Never infer membership from import counters or a page's complete flag. */
export function appendImportResultPage(
  previous: ChecklistImportResultManifest | null,
  page: ChecklistImportResultManifest,
): ChecklistImportResultManifest {
  if (!isImportResultPage(page)) throw new Error('Invalid result page.');
  if (
    previous &&
    (previous.batchId !== page.batchId ||
      previous.batchFingerprint !== page.batchFingerprint ||
      previous.membership.total !== page.membership.total ||
      previous.membership.coverage !== page.membership.coverage)
  )
    throw new Error('Batch membership changed. Reload results.');
  const products = [...(previous?.products ?? []), ...page.products];
  if (
    new Set(products.map((p) => p.sourceTargetId)).size !== products.length ||
    products.length > limits.maxBatchProducts ||
    (page.membership.total !== null && products.length > page.membership.total)
  )
    throw new Error('Duplicate or inconsistent batch membership.');
  const result = {
    ...page,
    products,
    membership: { ...page.membership, listed: products.length },
    complete:
      page.membership.coverage === 'COMPLETE' &&
      page.membership.total !== null &&
      !page.membership.nextCursor &&
      products.length === page.membership.total,
  };
  return result;
}
export function importResultsDownloadFits(value: unknown): boolean {
  return bounded(value, limits.maxLocalManifestBytes);
}
export function downloadImportResults(value: unknown, filename: string): void {
  const collection = shape({
    manifest: shape({ ...manifestFields, products: array(product, limits.maxBatchProducts) }),
    verificationReports: array(reportResponse, limits.maxBatchProducts),
    verificationReportsComplete: bool,
    applyAllowed: one(false),
  });
  const single = shape({
    schemaVersion: one(1),
    reportType: one('CHECKLIST_IMPORT_PRODUCT_RESULT'),
    batchId: id,
    batchFingerprint: id,
    product,
    verificationReport: nullable(reportResponse),
    applyAllowed: one(false),
  });
  if (!reportResponse(value) && !collection(value) && !single(value))
    throw new Error('Unsupported result download.');
  if (!bounded(value, limits.maxLocalManifestBytes))
    throw new Error('Results exceed the local download limit.');
  const url = URL.createObjectURL(new Blob([JSON.stringify(value)], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
export const CHECKLIST_LAST_RESULT_BATCH_KEY = 'tcvs.checklists.lastResultBatch';
export function rememberChecklistResultBatch(batchId: string): void {
  if (!id(batchId)) return;
  try {
    localStorage.setItem(CHECKLIST_LAST_RESULT_BATCH_KEY, batchId);
  } catch {
    /* Storage is optional. */
  }
}
export function readChecklistResultBatch(): string {
  try {
    const value = localStorage.getItem(CHECKLIST_LAST_RESULT_BATCH_KEY);
    return id(value) ? value! : '';
  } catch {
    return '';
  }
}
